import React, { useCallback, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { styled } from '@linaria/react';
import { HotelSummaryCardWithStore } from 'components/HotelSummaryCardWithStore';
import { useHotelCardActions } from 'hooks/useHotelCardActions';
import { useShowAccommodationPage } from 'hooks/useShowAccommodationPage';
import { getIsAnchorHotel } from 'modules/sapiSearch/selectors';
import { toggle } from 'opticks';
import { SelectedElement } from 'utils/selectedElement';
import { generateAccommodationPageURL } from 'utils/url';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { NoStyleButton } from '@daedalus/atlas/helpers/NoStyleButton';
import { getClosestDataIdToEventTarget } from '@daedalus/core/src/_web/utils/browser';
import { TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY } from '@daedalus/core/src/auth/modules/actions';
const PopperElement = /*#__PURE__*/styled('div')({
  name: "PopperElement",
  class: "p12ghv9v",
  propsAsIs: false
});
const ArrowElement = /*#__PURE__*/styled('div')({
  name: "ArrowElement",
  class: "au1kprq",
  propsAsIs: false
});
const _exp11 = /*#__PURE__*/() => NoStyleButton;
const FloatingWrapper = /*#__PURE__*/styled(_exp11())({
  name: "FloatingWrapper",
  class: "f1rdfl5g",
  propsAsIs: true
});
interface Props {
  hotelId: string;
  anchorNode?: HTMLElement;
  openAccommodationPageOnClick?: boolean;
  onCloseClick?: () => void;
}
export const MapHotelCard = ({
  hotelId,
  openAccommodationPageOnClick,
  onCloseClick,
  anchorNode
}: Props) => {
  const dispatch = useDispatch();
  const {
    search
  } = useLocation();
  const {
    isMobile
  } = useDeviceLayout();
  const {
    trackUserHotelCardClicked
  } = useHotelCardActions();
  const {
    showAccommodationPage
  } = useShowAccommodationPage();
  const [popperElement, setPopperElement] = useState<HTMLElement>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement>(null);
  const isAnchorHotel = useSelector(state => getIsAnchorHotel(state, hotelId));
  const hotelAccommodationPageURL = useMemo((): string => {
    return generateAccommodationPageURL(hotelId, search, isAnchorHotel);
  }, [search, hotelId, isAnchorHotel]);
  const handleClick = useCallback((e: React.MouseEvent) => {
    trackUserHotelCardClicked({
      hotelId: hotelId,
      selectedElement: getClosestDataIdToEventTarget(e) || SelectedElement.BlankSpace,
      component: 'MapViewHotelCard'
    });
    const openAccommodationPage = openAccommodationPageOnClick ?? toggle('377fd843-desktop-ap--v2', false, !isMobile);
    if (openAccommodationPage) {
      setTimeout(() => {
        window.open(hotelAccommodationPageURL, '_blank');
      }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY);
    } else {
      showAccommodationPage({
        hotelId
      });
    }
  }, [dispatch, hotelId, hotelAccommodationPageURL, isMobile, trackUserHotelCardClicked, showAccommodationPage]);
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCloseClick();
  };
  const {
    styles,
    attributes
  } = usePopper(anchorNode, popperElement, {
    modifiers: [{
      name: 'arrow',
      options: {
        element: arrowElement,
        padding: 8
      }
    }, {
      name: 'offset',
      options: {
        offset: [0, 3]
      }
    }, {
      name: 'preventOverflow',
      options: {
        padding: 8
      }
    }]
  });
  if (anchorNode) {
    return <PopperElement ref={setPopperElement} style={styles.popper} {...attributes.popper} onClick={handleClick}>
        <ArrowElement ref={setArrowElement} style={styles.arrow} className="popper-arrow" />
        <HotelSummaryCardWithStore isPopper hotelId={hotelId} onCloseClick={handleClose} />
      </PopperElement>;
  }
  return <FloatingWrapper role="button" onClick={handleClick}>
      <HotelSummaryCardWithStore hotelId={hotelId} onCloseClick={handleClose} />
    </FloatingWrapper>;
};

require("./MapHotelCard.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./MapHotelCard.tsx");